import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle, Circle, Copy, PaperPlaneTilt, LockSimpleOpen, Money, Printer, EnvelopeSimple, ArrowClockwise } from "@phosphor-icons/react";

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : null);
const OPENED = ["opened", "clicked"];
const DELIVERED = ["delivered", ...OPENED];

function downloadedLabel(project) {
  const released = (project.deliveries || []).filter((d) => d.released);
  const got = new Set((project.delivery_tracking?.downloads || []).map((x) => x.delivery_id));
  const n = released.filter((d) => got.has(d.delivery_id)).length;
  return released.length > 1 ? `Downloaded ${n} of ${released.length}` : "Downloaded";
}

function FileReceipts({ files, downloads, pickedUp }) {
  if (!files.length) return null;
  return (
    <div className="rounded-md border border-slate-200 p-3" data-testid="delivery-file-receipts">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Files</div>
      {files.map((f) => {
        const hits = downloads.filter((x) => x.delivery_id === f.delivery_id);
        const last = hits[hits.length - 1];
        return (
          <div key={f.delivery_id} className="flex items-start justify-between gap-3 py-1.5 border-t border-slate-100 first:border-0" data-testid={`file-receipt-${f.delivery_id}`}>
            <div className="min-w-0">
              <div className="text-sm text-slate-900 truncate">{f.filename}</div>
              <div className="text-xs text-slate-500">{f.released ? (last ? `Last downloaded ${fmtDT(last.at)}` : pickedUp ? "Handed over in person" : "Waiting for client") : "Locked until payment"}</div>
            </div>
            <span className={`badge-pill shrink-0 ${hits.length ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
              {hits.length ? `Downloaded ${hits.length}×` : "Not downloaded"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function receiptHtml(r) {
  const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const money = (n) => `$${Number(n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  const rows = r.invoices.map((i) => `<tr><td>${esc(i.invoice_number)}</td><td style="text-align:right">${money(i.amount)}</td></tr>`).join("");
  return `<!doctype html><html><head><title>Receipt ${esc(r.receipt_number)}</title><style>
    body{font-family:Georgia,serif;color:#0F172A;max-width:640px;margin:40px auto;padding:0 24px}
    h1{font-size:22px;margin:0}.muted{color:#64748B;font-size:12px}table{width:100%;border-collapse:collapse;margin-top:12px}
    td{padding:6px 0;border-bottom:1px solid #E2E8F0;font-size:14px}.tot td{font-weight:700;border-bottom:none}
    .box{border:1px solid #CBD5E1;border-radius:8px;padding:16px 20px;margin-top:20px}@media print{button{display:none}}
  </style></head><body>
    <div style="display:flex;justify-content:space-between;align-items:flex-start">
      <div><h1>${esc(r.company_name)}</h1><div class="muted">${esc(r.company_address || "")}<br/>${esc(r.company_phone || "")} · ${esc(r.company_email || "")}</div></div>
      <div style="text-align:right"><div style="font-size:18px;font-weight:700">RECEIPT</div><div class="muted">${esc(r.receipt_number)}</div></div>
    </div>
    <div class="box">
      <div><b>Received from:</b> ${esc(r.client_name || "—")}</div>
      <div><b>Date:</b> ${esc(new Date(r.picked_up_at).toLocaleString())}</div>
      <div><b>Payment method:</b> Cash · in-person pickup</div>
      ${r.picked_up_by ? `<div><b>Picked up by:</b> ${esc(r.picked_up_by)}</div>` : ""}
      <div style="margin-top:10px"><b>Project:</b> ${esc(r.project_title)} (${esc(r.source_language || "—")} → ${esc(r.target_language || "—")})</div>
      <div><b>Documents:</b><br/>${r.files.map(esc).join("<br/>") || "—"}</div>
      <table>${rows}<tr class="tot"><td>Total paid</td><td style="text-align:right">${money(r.total_paid)}</td></tr></table>
    </div>
    <p class="muted" style="margin-top:24px">Thank you for choosing ${esc(r.company_name)}.</p>
    <button onclick="window.print()" style="padding:8px 16px">Print</button>
  </body></html>`;
}

function ReceiptActions({ pid, clientEmail }) {
  const print = async () => {
    const w = window.open("", "_blank");
    try {
      const r = (await api.get(`/projects/${pid}/pickup-receipt`)).data;
      w.document.write(receiptHtml(r));
      w.document.close();
      setTimeout(() => w.print(), 300);
    } catch (e) {
      w?.close();
      toast.error(e?.response?.data?.detail || "Could not load receipt");
    }
  };
  const email = useMutation({
    mutationFn: async () => (await api.post(`/projects/${pid}/pickup-receipt/email`, {})).data,
    onSuccess: (d) => toast.success(`Receipt emailed to ${d.sent_to}`),
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not email receipt"),
  });
  return (
    <div className="flex gap-2 mt-2">
      <Button size="sm" variant="outline" onClick={print} data-testid="receipt-print-btn"><Printer size={14} className="mr-1.5" /> Print receipt</Button>
      <Button size="sm" variant="outline" onClick={() => email.mutate()} disabled={email.isPending || !clientEmail} data-testid="receipt-email-btn">
        <EnvelopeSimple size={14} className="mr-1.5" /> {email.isPending ? "Sending…" : "Email receipt"}
      </Button>
    </div>
  );
}

function CashPickup({ pid, onDone }) {
  const [name, setName] = useState("");
  const [emailFiles, setEmailFiles] = useState(false);
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/projects/${pid}/cash-pickup`, { picked_up_by: name || null, email_files: emailFiles })).data,
    onSuccess: (d) => {
      toast.success(`Cash payment recorded${d.invoices_paid.length ? ` (${d.invoices_paid.join(", ")})` : ""} — marked picked up${emailFiles ? (d.emailed ? " and files emailed" : " — email failed, use Resend") : ""}`);
      onDone();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not record pickup"),
  });
  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-2" data-testid="cash-pickup-section">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">In-person pickup · paid in cash</div>
      <Input placeholder="Picked up by (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="cash-pickup-name" />
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={emailFiles} onChange={(e) => setEmailFiles(e.target.checked)} data-testid="cash-pickup-email-files" />
        Also email the download links
      </label>
      <Button onClick={() => window.confirm("Mark the open invoice paid in cash and record the pickup?") && mut.mutate()}
        disabled={mut.isPending} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cash-pickup-btn">
        <Money size={16} className="mr-1.5" /> {mut.isPending ? "Saving…" : "Mark paid in cash & picked up"}
      </Button>
    </div>
  );
}

function reminderLine(t, downloads, status) {
  if (!t.last_sent_at) return null;
  if (t.picked_up_at) return "No reminders — picked up in person";
  const sent = [t.reminder_48h_sent_at && `48-hour reminder sent ${fmtDT(t.reminder_48h_sent_at)}`,
    t.reminder_5d_sent_at && `5-day reminder sent ${fmtDT(t.reminder_5d_sent_at)}`].filter(Boolean);
  if (downloads.length || t.confirmed_received_at) return sent.length ? `${sent.join(" · ")} · reminders stopped` : "No reminders needed";
  if (t.reminder_5d_sent_at) return sent.join(" · ");
  const base = new Date(t.first_sent_at || t.last_sent_at).getTime();
  if (status === "cancelled" || Date.now() - base > 14 * 24 * 3600e3) return sent.length ? sent.join(" · ") : "No automatic reminders (older than 14 days)";
  const next = t.reminder_48h_sent_at ? base + 120 * 3600e3 : base + 48 * 3600e3;
  return [...sent, `Next reminder ${fmtDT(new Date(next).toISOString())} if nothing is downloaded`].join(" · ");
}

export function DeliveryBadge({ project }) {
  const deliveries = project.deliveries || [];
  if (!deliveries.length) return null;
  const t = project.delivery_tracking || {};
  if (project.awaiting_payment_release && !project.delivery_tracking?.picked_up_at) {
    return <span className="badge-pill bg-amber-50 text-amber-800 border-amber-200 mt-1" data-testid={`delivery-badge-${project.project_id}`}>Held · awaiting payment</span>;
  }
  const label = t.picked_up_at ? "Picked up in person"
    : t.confirmed_received_at ? "Client confirmed"
    : (t.downloads || []).length ? downloadedLabel(project)
    : (t.first_opened_at || OPENED.includes(t.email_status)) ? "Email opened"
    : "Released";
  return <span className="badge-pill bg-emerald-50 text-emerald-800 border-emerald-200 mt-1" data-testid={`delivery-badge-${project.project_id}`}>{label}</span>;
}

function Step({ done, label, when, testid }) {
  return (
    <div className="flex items-start gap-2.5 py-1.5" data-testid={testid}>
      {done ? <CheckCircle size={18} weight="fill" className="text-emerald-600 mt-0.5" /> : <Circle size={18} className="text-slate-300 mt-0.5" />}
      <div>
        <div className={`text-sm ${done ? "text-slate-900 font-medium" : "text-slate-400"}`}>{label}</div>
        {when && <div className="text-xs text-slate-500">{when}</div>}
      </div>
    </div>
  );
}

export function DeliveryTrackingDialog({ project, clientEmail, onClose }) {
  const qc = useQueryClient();
  const [to, setTo] = useState("");
  useEffect(() => { setTo(clientEmail || ""); setAttach(true); setShowPickup(false); }, [clientEmail, project?.project_id]);
  const pid = project?.project_id;
  const { data, isLoading } = useQuery({
    queryKey: ["delivery-status", pid], enabled: !!pid,
    queryFn: async () => (await api.get(`/projects/${pid}/delivery-status`)).data,
  });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["delivery-status", pid] }); qc.invalidateQueries({ queryKey: ["projects"] }); };
  const [attach, setAttach] = useState(true);
  const [showPickup, setShowPickup] = useState(false);
  const resend = useMutation({
    mutationFn: async () => (await api.post(`/projects/${pid}/resend-translation`, { to: to || null, attach_files: attach })).data,
    onSuccess: (d) => { toast.success(d.attached ? `Clean files (${d.attached}) + links sent to ${d.sent_to}` : `Download link sent to ${d.sent_to}`); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Resend failed"),
  });
  const preview = useMutation({
    mutationFn: async () => (await api.post(`/projects/${pid}/resend-preview`, {})).data,
    onSuccess: (d) => { toast.success(`Preview + new pay link sent to ${d.sent_to}`); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Preview resend failed"),
  });
  const release = useMutation({
    mutationFn: async () => (await api.post(`/projects/${pid}/release-translation`)).data,
    onSuccess: () => { toast.success("Released — download link emailed to client"); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Release failed"),
  });

  const t = data?.tracking || {};
  const released = (data?.deliveries || []).filter((d) => d.released);
  const downloads = t.downloads || [];
  const opened = t.first_opened_at || OPENED.includes(t.email_status) || downloads.length > 0 || !!t.confirmed_received_at;
  const inbox = DELIVERED.includes(t.email_status) || !!opened;
  return (
    <Dialog open={!!project} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="delivery-tracking-dialog">
        <DialogHeader><DialogTitle>Delivery tracking — {project?.title}</DialogTitle></DialogHeader>
        {isLoading || !data ? <div className="text-sm text-slate-400">Loading…</div> : (
          <div className="space-y-4 text-sm">
            <div className="rounded-md border border-slate-200 p-3">
              <Step testid="step-released" done={released.length > 0} label={released.length ? `Released after payment (${released.length} file${released.length > 1 ? "s" : ""})` : "Held — waiting for payment"} when={fmtDT(released[0]?.released_at)} />
              {t.picked_up_at && !t.last_sent_at ? (
                <Step testid="step-picked-up" done label={`Handed over in person${t.picked_up_by ? ` to ${t.picked_up_by}` : ""} — no email needed`} when={fmtDT(t.picked_up_at)} />
              ) : (<>
              <Step testid="step-sent" done={!!t.last_sent_at} label={t.last_sent_at ? `Download link emailed to ${t.last_sent_to}${t.send_count > 1 ? ` · sent ${t.send_count}×` : ""}` : "Download link not sent yet"} when={fmtDT(t.last_sent_at)} />
              <Step testid="step-delivered" done={inbox} label={t.email_status === "bounced" ? "Email bounced — try another address" : inbox ? "Reached their inbox" : "Not yet confirmed in their inbox"} when={t.email_status ? `Status: ${t.email_status}` : null} />
              <Step testid="step-opened" done={!!opened} label={opened ? `Email opened${t.open_count > 1 ? ` · ${t.open_count}×` : ""}` : "Email not opened yet"} when={fmtDT(t.first_opened_at)} />
              <Step testid="step-downloaded" done={downloads.length > 0} label={downloads.length ? `Downloaded ${downloads.length}×` : "Not downloaded yet"} when={downloads.length ? `Last: ${fmtDT(downloads[downloads.length - 1].at)}` : null} />
              <Step testid="step-confirmed" done={!!t.confirmed_received_at} label={t.confirmed_received_at ? `Client clicked "I received it"${t.confirmed_name ? ` — ${t.confirmed_name}` : ""}` : "Client hasn't confirmed receipt"} when={fmtDT(t.confirmed_received_at)} />
              </>)}
            </div>
            {t.picked_up_at ? (
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800" data-testid="cash-pickup-done">
                Paid in cash · picked up {fmtDT(t.picked_up_at)}{t.picked_up_by ? ` by ${t.picked_up_by}` : ""}
                {t.receipt_emailed_at && <div className="text-xs mt-1">Receipt emailed {fmtDT(t.receipt_emailed_at)} to {t.receipt_emailed_to}</div>}
                <ReceiptActions pid={pid} clientEmail={clientEmail} />
              </div>
            ) : (data.awaiting_payment_release || showPickup) ? <CashPickup pid={pid} onDone={refresh} /> : (
              <button type="button" onClick={() => setShowPickup(true)} className="text-xs text-[#1E3A8A] hover:underline" data-testid="show-cash-pickup-btn">
                Record an in-person pickup…
              </button>
            )}
            <FileReceipts files={data.deliveries || []} downloads={downloads} pickedUp={!!t.picked_up_at} />
            {reminderLine(t, downloads, project?.status) && (
              <div className="text-xs text-slate-600 px-1" data-testid="delivery-reminder-status">{reminderLine(t, downloads, project?.status)}</div>
            )}
            {data.awaiting_payment_release ? (
              <div className="space-y-2">
                <Button onClick={() => preview.mutate()} disabled={preview.isPending} variant="outline" className="w-full" data-testid="delivery-resend-preview-btn">
                  <ArrowClockwise size={16} className="mr-1.5" /> {preview.isPending ? "Sending…" : "Resend watermarked preview + new pay link"}
                </Button>
                {t.preview_last_sent_at && (
                  <div className="text-xs text-slate-500" data-testid="delivery-preview-last-sent">Preview last resent {fmtDT(t.preview_last_sent_at)} to {t.preview_last_sent_to}</div>
                )}
                <Button onClick={() => window.confirm("Release the clean file now? Use this when the client paid by Zelle, cash or check.") && release.mutate()}
                  disabled={release.isPending} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="delivery-release-now-btn">
                  <LockSimpleOpen size={16} className="mr-1.5" /> {release.isPending ? "Releasing…" : "Release now (paid another way)"}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Resend clean (unblurred) translation to</div>
                <div className="flex gap-2">
                  <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} data-testid="delivery-resend-email" />
                  <Button onClick={() => resend.mutate()} disabled={resend.isPending || !to} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="delivery-resend-btn">
                    <PaperPlaneTilt size={16} className="mr-1.5" /> {resend.isPending ? "Sending…" : "Resend"}
                  </Button>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={attach} onChange={(e) => setAttach(e.target.checked)} data-testid="delivery-resend-attach" />
                  Attach the clean files to the email (plus download links)
                </label>
                {data.download_link && (
                  <Button variant="outline" className="w-full" data-testid="delivery-copy-link-btn"
                    onClick={() => { navigator.clipboard.writeText(data.download_link); toast.success("Link copied — share it by text or WhatsApp"); }}>
                    <Copy size={16} className="mr-1.5" /> Copy download link
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
